import {
  Component,
  ElementRef,
  inject,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';

import {
  ObserveIntersectingDirective,
  ProgressComponent,
  IconComponent,
  MessageService
} from '@factor_ec/ui';
import { Operation, Option } from '@factor_ec/utils';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

import { AppService } from '@/core/app.service';
import { PaymentType } from '@/main/models/payment';
import { ApiService } from '@/core/api.service';
import { Response } from '@/main/models/response';
import { Credit } from '@/main/models/credit';
import { Settings } from '@/core/models/settings';
import { SessionService } from '@/core/session.service';
import moment from 'moment';
import { distinctUntilChanged } from 'rxjs/operators';
import { twoDecimalPlacesValidator } from '@/main/utils/twoDecimalPlacesValidator';
import { toBlob, toPng } from 'html-to-image';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { RequestData } from '@/core/models/request-data';

type TicketType = 'NOR' | 'PRE' | 'PAC' | 'APZ' | 'ASL';

/**
 * @description Validador que verifica que el valor sea mayor que un mínimo
 * @param {number} minValue - Valor mínimo permitido (exclusivo)
 * @returns {ValidatorFn} Función validadora de Angular
 *
 * @example
 * this.formBuilder.group({
 *   amount: ['', [greaterThan(100)]]
 * });
 */
export function greaterThan(minValue: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value !== null && control.value <= minValue) {
      return { greaterThan: { min: minValue, actual: control.value } };
    }
    return null;
  };
}

/**
 * @description Validador que verifica que el valor sea menor que un máximo
 * @param {number} maxValue - Valor máximo permitido (exclusivo)
 * @returns {ValidatorFn} Función validadora de Angular
 *
 * @example
 * this.formBuilder.group({
 *   amount: ['', [lessThan(1000)]]
 * });
 */
export function lessThan(maxValue: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value !== null && control.value >= maxValue) {
      return { lessThan: { max: maxValue, actual: control.value } };
    }
    return null;
  };
}

/**
 * @description Componente para gestionar pagos de créditos
 * @class PaymentComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-payment',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormField,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatSelectModule,
    ObserveIntersectingDirective,
    IconComponent,
    ProgressComponent
  ],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentComponent implements OnInit {
  // Dependency injection
  private readonly apiService = inject(ApiService);
  public readonly appService = inject(AppService);
  private readonly sessionService = inject(SessionService);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private paymentElement =
    viewChild<ElementRef<HTMLDivElement>>('paymentElement');

  // Events
  afterSubmit = output<Operation>();

  // Properties
  additionalPaymentTypes = signal<Option[]>([
    { label: $localize`Reduction of term`, value: 'APZ' },
    { label: $localize`Reduction of installment`, value: 'ASL' }
  ]);
  allowSharing = signal<boolean>(false);
  credit!: Credit;
  currencyId = signal<string>('');
  form: FormGroup = new FormGroup({});
  messageCommaNotAllowed = signal<string>($localize`Comma not allowed`);
  minDate = signal<Date | undefined>(undefined);
  pendingAmount = signal<any | undefined>(undefined);
  prePaymentCalculate = signal<boolean>(false);
  submitting = signal<boolean>(false);
  ticketNumber = signal<number | undefined>(undefined);
  type = signal<PaymentType>('regularPayment');
  today = new Date();
  totalAmount = signal<number | undefined>(undefined);
  maxDate = signal<Date | undefined>(undefined);
  sharing = signal<boolean>(false);
  canvas!: HTMLCanvasElement;

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  ngOnInit(): void {
    this.type.set(this.data.type);
    this.initForm();
    if (this.data.type === 'additionalPayment') {
      this.form
        .get('additionalPaymentType')
        ?.setValue(this.additionalPaymentTypes()[0]);
      this.form
        .get('additionalPaymentType')
        ?.setValidators([Validators.required]);
    }
    this.credit = structuredClone(this.data.credit);
    this.pendingAmount.set(this.data.credit.amountOverdueToDate || 0);
    this.getValidationRules(this.data.credit.countryId);
    this.allowSharing.set(
      // Capacitor.isNativePlatform() || typeof navigator.share === 'function'
      Capacitor.isNativePlatform()
    );
  }

  /**
   * @description Calcula el pago previo
   * @returns {Promise<void>}
   */
  async calcPrePayment(): Promise<void> {
    try {
      this.submitting.set(true);
      this.form.disable();
      const response = await this.getPendingAmount(
        this.credit,
        this.getDate(this.form.get('paymentDate')?.value)
      );
      if (response) {
        this.totalAmount.set(response.data.amountOverdue);
        this.form.get('amount')?.setValue(this.totalAmount());
        this.prePaymentCalculate.set(true);
      }
    } finally {
      this.form.enable();
      this.submitting.set(false);
    }
  }

  /**
   * @description Obtiene el monto pendiente
   * @param {Credit} credit - Crédito actual
   * @param {string} paymentDate - Fecha de pago
   * @returns {Promise<Response>} - Respuesta de la llamada API
   */
  async getPendingAmount(
    credit: Credit,
    paymentDate: string,
    ticketType: string = 'PRE'
  ): Promise<Response | null> {
    try {
      return await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: '/payment/simulate-payment',
        method: 'POST',
        useToken: true,
        body: {
          countryId: credit.countryId,
          productTypeId: 'CON',
          operationNumber: credit.operationNumber,
          paymentDate,
          ticketType
        },
        bodyEncrypted: true
      });
    } catch (err) {
      this.dialogRef.close();
      return null;
    }
  }

  /**
   * @description Obtiene la fecha en formato ISO
   * @param {Date} date - Fecha a formatear
   * @returns {string} - Fecha formateada
   */
  getDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses son 0-indexados
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * @description Obtiene el tipo de ticket
   * @returns {TicketType} - Tipo de ticket
   */
  getTicketType(): TicketType {
    let ticketType: TicketType = 'NOR';
    if (this.type() === 'prePayment') {
      ticketType = 'PRE';
    } else if (
      this.type() === 'regularPayment' &&
      this.form.get('amount')?.value > this.pendingAmount()
    ) {
      ticketType = 'PAC';
    } else if (this.type() === 'additionalPayment') {
      ticketType = this.form.get('additionalPaymentType')?.value.value;
    }
    return ticketType;
  }

  /**
   * @description Obtiene las reglas de validación
   * @param {string} countryId - ID del país
   * @returns {Promise<void>}
   */
  async getValidationRules(countryId: string): Promise<void> {
    const settings = this.sessionService.settings() as Settings;
    const countries = this.sessionService
      .catalogs()
      .filter(c => c.catalogId === 'CPAMD');
    const country = countries?.find(c => c.id === countryId);
    const subscriptionLevelId = this.sessionService.params()['profileId'];
    // Si es una precancelación si puede ingresar una fecha futura
    this.maxDate.set(this.type() === 'prePayment' ? undefined : new Date());
    if (country) {
      // Actualiza el id de moneda
      this.currencyId.set(country.value);
      // Actualiza reglas de solicitud
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPApplicationSettings',
        resourcePath: `/appsrv/get-validation-rules/${country.value}/CON/${subscriptionLevelId}`,
        method: 'GET'
      });
      const rules = response.data.find((r: any) => r.countryId === country.id);
      if (rules) {
        const minDateRule = rules.validations.find(
          (r: any) => r.id === 'MPFAP'
        );
        let minDate = moment();
        if (minDateRule) {
          minDate.subtract(minDateRule.value, 'months');
        }
        const effectiveDate = moment(this.credit.effectiveDate);
        // Solo si amountOverdueToDate > 0 se considera firstDatePending como fecha mínima
        const firstDatePending =
          this.credit.amountOverdueToDate > 0
            ? moment(this.credit.firstDatePending)
            : null;
        const lastDatePayment = this.credit.lastDatePayment
          ? moment(this.credit.lastDatePayment)
          : null;
        const dates = [
          minDate,
          firstDatePending,
          lastDatePayment,
          effectiveDate
        ].filter(date => date !== null);
        // Si hay fechas válidas, devolver la fecha más alta
        if (dates.length > 0) {
          this.minDate.set(moment.max(...dates).toDate());
        }
      }
    }
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private initForm(): void {
    this.form = this.formBuilder.group({
      amount: [
        this.data.type === 'regularPayment'
          ? this.data.credit.amountOverdueToDate
          : '',
        [
          Validators.required,
          Validators.min(1),
          twoDecimalPlacesValidator(),
          Validators.pattern(/^[0-9]+(\.[0-9]*)?$/)
        ]
      ],
      paymentDate: [new Date(), Validators.required],
      ticketNumber: '',
      additionalPaymentType: ''
    });
    this.form
      .get('paymentDate')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(async value => {
        if (
          this.form.enabled &&
          ((this.type() !== 'prePayment' && value !== this.today) ||
            (this.type() === 'prePayment' && this.prePaymentCalculate()))
        ) {
          switch (this.type()) {
            case 'regularPayment':
              if (
                moment(value).startOf('day').isBefore(moment().startOf('day'))
              ) {
                this.submitting.set(true);
                const simulation: any = await this.getPendingAmount(
                  this.credit,
                  this.getDate(value),
                  'NOR'
                );
                if (simulation) {
                  this.pendingAmount.set(simulation.data.amountOverdue);
                  this.form.get('amount')?.setValue(this.pendingAmount());
                  this.submitting.set(false);
                }
              } else {
                this.pendingAmount.set(this.credit.amountOverdueToDate);
              }
              break;
            case 'additionalPayment':
              this.submitting.set(true);
              const additionalPaymentResponse = await this.getPendingAmount(
                this.credit,
                this.getDate(value)
              );
              if (additionalPaymentResponse) {
                this.submitting.set(false);
                this.totalAmount.set(
                  additionalPaymentResponse.data.principalAmortization
                );
                this.form
                  .get('amount')
                  ?.setValidators([
                    greaterThan(this.data.credit.amountOverdueToDate),
                    lessThan(this.totalAmount() || 0)
                  ]);
                this.form.get('amount')?.updateValueAndValidity();
              }
              break;
            default:
              this.submitting.set(true);
              const defaultResponse = await this.getPendingAmount(
                this.credit,
                this.getDate(value)
              );
              if (defaultResponse) {
                this.submitting.set(false);
                this.totalAmount.set(defaultResponse.data.amountOverdue);
                this.form.get('amount')?.setValue(this.totalAmount());
              }
              break;
          }
        }
      });
  }
  /**
   * @description Comparte el contenido
   * @returns {Promise<void>}
   */
  async share(download?: boolean): Promise<void> {
    const fileName = $localize`receipt` + `-${this.ticketNumber()}.png`;
    if (Capacitor.isNativePlatform()) {
      this.nativeShare(fileName);
      return;
    }
    const timerStart = performance.now();
    // Crear contenedor en memoria sin modificar el DOM hasta estar listo
    const paymentElement = this.paymentElement()?.nativeElement;
    if (paymentElement) {
      setTimeout(async () => {
        const isFirefox =
          navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        const blob = await toBlob(paymentElement, {
          skipFonts: isFirefox
        });
        // console.log(
        //   'File generated in:',
        //   (performance.now() - timerStart).toFixed(2),
        //   'ms'
        // );
        if (blob) {
          if (!download) {
            const file = new File([blob], fileName, {
              type: 'image/png'
            });
            await navigator.share({
              files: [file],
              title: '',
              text: ''
            });
          } else {
            // Crear un enlace temporal y descargar el archivo
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName; // Cambia el nombre del archivo según tus necesidades.
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Liberar el objeto URL
            URL.revokeObjectURL(url);
          }
        }
      });
    }
  }

  /**
   * @description Comparte el comprobante de pago usando la API nativa de Capacitor
   * @param {string} fileName - Nombre del archivo a compartir
   * @returns {Promise<void>}
   */
  async nativeShare(fileName: string): Promise<void> {
    const paymentElement = this.paymentElement()?.nativeElement;
    if (paymentElement) {
      setTimeout(async () => {
        const data = await toPng(paymentElement);
        const path = fileName;
        await Filesystem.writeFile({
          path,
          data,
          directory: Directory.Cache
        });
        const uriResult = await Filesystem.getUri({
          directory: Directory.Cache,
          path
        });
        Share.share({
          files: [uriResult.uri],
          title: ''
        });
      });
    }
  }
  /**
   * @description Envía el formulario para procesar el pago
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.get('paymentDate')?.value.getTime() > new Date().getTime()) {
      this.messageService.show(
        $localize`To save a payment, the date must be today or earlier.`,
        { class: 'ft-message--danger', icon: 'warning' }
      );
      return;
    }
    if (this.form.valid) {
      try {
        const lenderId = this.sessionService.settings()?.lenderId;
        const data: RequestData = {
          microServiceName: 'WSMPCreditManagement',
          resourcePath: '/payment/process-payment',
          method: 'POST',
          useToken: true,
          body: {
            lenderId: lenderId,
            countryId: this.data.credit.countryId,
            productTypeId: 'CON',
            beneficiary: this.credit.beneficiary,
            operationNumber: this.credit.operationNumber,
            paymentDate: this.getDate(this.form.get('paymentDate')?.value),
            ticketType: this.getTicketType(),
            amount: this.form.get('amount')?.value
          },
          bodyEncrypted: true
        };
        this.form.disable();
        this.submitting.set(true);
        this.dialogRef.disableClose = true;
        const response = await this.apiService.call(data);
        if (this.data?.credit) {
          this.data.credit.amountRecoveredToDate =
            response.data.amountRecoveredToDate;
          this.data.credit.amountOverdueToDate =
            response.data.amountOverdueToDate;
          this.data.credit.interestEarnedToDate =
            response.data.interestEarnedToDate;
          this.data.credit.expirationDate = response.data.dueDateCredit;
        }
        this.ticketNumber.set(response.data.ticketNumber);
        this.submitting.set(false);
        this.dialogRef.disableClose = false;
        this.afterSubmit.emit({
          method: 'add',
          entity: { ticketType: this.getTicketType(), ...response.data }
        });
      } finally {
        this.form.enable();
        this.submitting.set(false);
        this.dialogRef.disableClose = false;
      }
    }
  }
}
